import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, MoreVertical, FolderTree, Package } from 'lucide-react';
import { PageHeader } from '../../components/layout';
import {
    Card, CardHeader, CardBody, Table, Pagination, Button, Badge,
    SearchInput, Select, Dropdown, DropdownItem, DropdownDivider,
    Modal, ConfirmDialog, toast, Tabs
} from '../../components/common';
import { catalogApi } from '../../services/api';
import CatalogItemForm from './CatalogItemForm';
import CategoryForm from './CategoryForm';
import './Catalog.css';

export default function CatalogList() {
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState('items');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');

    const [showItemForm, setShowItemForm] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [deleteItem, setDeleteItem] = useState(null);

    const [showCategoryForm, setShowCategoryForm] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);

    // Fetch catalog items
    const { data: itemsData, isLoading: itemsLoading } = useQuery({
        queryKey: ['catalogItems', page, pageSize, search, categoryFilter],
        queryFn: () => catalogApi.listItems({ page, limit: pageSize, search, categoryId: categoryFilter }),
        select: (res) => res.data,
        enabled: activeTab === 'items'
    });

    // Fetch categories
    const { data: categoriesData, isLoading: categoriesLoading } = useQuery({
        queryKey: ['categories'],
        queryFn: () => catalogApi.listCategories({}),
        select: (res) => res.data
    });

    // Delete item mutation
    const deleteItemMutation = useMutation({
        mutationFn: (id) => catalogApi.deleteItem(id),
        onSuccess: () => {
            toast.success('Item deleted successfully');
            queryClient.invalidateQueries(['catalogItems']);
            setDeleteItem(null);
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Failed to delete item');
        }
    });

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(value || 0);
    };

    const itemColumns = [
        {
            key: 'code',
            title: 'Code',
            dataIndex: 'itemCode',
            width: 120,
            render: (code) => <span className="item-code">{code}</span>
        },
        {
            key: 'name',
            title: 'Item Name',
            dataIndex: 'name',
            render: (name, row) => (
                <div className="item-cell">
                    <span className="item-name">{name}</span>
                    <span className="item-desc">{row.description?.substring(0, 50)}...</span>
                </div>
            )
        },
        {
            key: 'category',
            title: 'Category',
            dataIndex: 'category',
            render: (cat) => cat?.name || '-'
        },
        {
            key: 'uom',
            title: 'UOM',
            dataIndex: 'unitOfMeasure',
            width: 80,
            render: (uom) => uom?.code || '-'
        },
        {
            key: 'price',
            title: 'Unit Price',
            dataIndex: 'unitPrice',
            align: 'right',
            render: (price) => formatCurrency(price)
        },
        {
            key: 'status',
            title: 'Status',
            dataIndex: 'isActive',
            width: 100,
            render: (isActive) => (
                <Badge variant={isActive ? 'success' : 'default'}>
                    {isActive ? 'Active' : 'Inactive'}
                </Badge>
            )
        },
        {
            key: 'actions',
            title: '',
            width: 50,
            align: 'center',
            render: (_, row) => (
                <Dropdown
                    trigger={<button className="action-btn"><MoreVertical size={16} /></button>}
                    align="right"
                >
                    {({ close }) => (
                        <>
                            <DropdownItem icon={Edit2} onClick={() => { close(); setEditingItem(row); setShowItemForm(true); }}>
                                Edit
                            </DropdownItem>
                            <DropdownDivider />
                            <DropdownItem icon={Trash2} danger onClick={() => { close(); setDeleteItem(row); }}>
                                Delete
                            </DropdownItem>
                        </>
                    )}
                </Dropdown>
            )
        }
    ];

    const categoryOptions = [
        { value: '', label: 'All Categories' },
        ...((categoriesData?.data || []).map(c => ({ value: c.id.toString(), label: c.name })))
    ];

    const tabs = [
        { key: 'items', label: 'Catalog Items', icon: Package },
        { key: 'categories', label: 'Categories', icon: FolderTree }
    ];

    return (
        <div className="catalog-page">
            <PageHeader
                title="Catalog Management"
                subtitle="Manage catalog items and categories"
                breadcrumbs={[{ label: 'Catalog', path: '/catalog' }]}
                actions={
                    activeTab === 'items' ? (
                        <Button icon={Plus} onClick={() => setShowItemForm(true)}>
                            Add Item
                        </Button>
                    ) : (
                        <Button icon={Plus} onClick={() => setShowCategoryForm(true)}>
                            Add Category
                        </Button>
                    )
                }
            />

            <Card>
                <div className="catalog-tabs">
                    {tabs.map(tab => (
                        <button
                            key={tab.key}
                            className={`catalog-tab ${activeTab === tab.key ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            <tab.icon size={16} />
                            {tab.label}
                        </button>
                    ))}
                </div>

                {activeTab === 'items' && (
                    <>
                        <div className="catalog-toolbar">
                            <SearchInput
                                value={search}
                                onChange={setSearch}
                                onSearch={setSearch}
                                placeholder="Search items..."
                                className="catalog-search"
                            />
                            <Select
                                value={categoryFilter}
                                onChange={(e) => setCategoryFilter(e.target.value)}
                                options={categoryOptions}
                                className="catalog-filter"
                            />
                        </div>

                        <Table
                            columns={itemColumns}
                            data={itemsData?.data || []}
                            loading={itemsLoading}
                            emptyText="No catalog items found"
                            rowKey="id"
                        />

                        {itemsData?.meta && (
                            <div className="catalog-pagination">
                                <Pagination
                                    page={page}
                                    totalPages={itemsData.meta.totalPages}
                                    pageSize={pageSize}
                                    total={itemsData.meta.total}
                                    onPageChange={setPage}
                                    onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                                />
                            </div>
                        )}
                    </>
                )}

                {activeTab === 'categories' && (
                    <CardBody>
                        <div className="categories-grid">
                            {(categoriesData?.data || []).map(category => (
                                <div key={category.id} className="category-card">
                                    <div className="category-icon">
                                        <FolderTree size={24} />
                                    </div>
                                    <div className="category-info">
                                        <h4>{category.name}</h4>
                                        <p>{category.code}</p>
                                    </div>
                                    <button
                                        className="category-edit"
                                        onClick={() => { setEditingCategory(category); setShowCategoryForm(true); }}
                                    >
                                        <Edit2 size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </CardBody>
                )}
            </Card>

            {/* Item Form Modal */}
            <Modal
                isOpen={showItemForm}
                onClose={() => { setShowItemForm(false); setEditingItem(null); }}
                title={editingItem ? 'Edit Item' : 'Add New Item'}
                size="lg"
            >
                <CatalogItemForm
                    item={editingItem}
                    categories={categoriesData?.data || []}
                    onSuccess={() => {
                        setShowItemForm(false);
                        setEditingItem(null);
                        queryClient.invalidateQueries(['catalogItems']);
                    }}
                    onCancel={() => { setShowItemForm(false); setEditingItem(null); }}
                />
            </Modal>

            {/* Category Form Modal */}
            <Modal
                isOpen={showCategoryForm}
                onClose={() => { setShowCategoryForm(false); setEditingCategory(null); }}
                title={editingCategory ? 'Edit Category' : 'Add New Category'}
                size="md"
            >
                <CategoryForm
                    category={editingCategory}
                    onSuccess={() => {
                        setShowCategoryForm(false);
                        setEditingCategory(null);
                        queryClient.invalidateQueries(['categories']);
                    }}
                    onCancel={() => { setShowCategoryForm(false); setEditingCategory(null); }}
                />
            </Modal>

            {/* Delete Confirmation */}
            <ConfirmDialog
                isOpen={!!deleteItem}
                onClose={() => setDeleteItem(null)}
                onConfirm={() => deleteItemMutation.mutate(deleteItem?.id)}
                title="Delete Item"
                message={`Are you sure you want to delete "${deleteItem?.name}"?`}
                confirmText="Delete"
                variant="danger"
                loading={deleteItemMutation.isPending}
            />
        </div>
    );
}
